import { useStore } from '@tanstack/react-form';
import { useFieldContext } from './formContext';
import { ReactNode } from 'react';
import { last } from 'lodash-es';

interface UseReactFormPropsMapperProps {
  helperText?: ReactNode;
}

interface UseInputPropsReturn<TData> {
  id: string;
  name: string;
  value: TData;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
  onBlur: () => void;
  disabled: boolean;
  error: boolean;
}

export function useInputProps<TData>(): UseInputPropsReturn<TData> {
  const field = useFieldContext<TData>();
  const isSubmitting = useStore(field.form.store, (state) => state.isSubmitting);
  return {
    id: field.name,
    name: field.name,
    value: field.state.value,
    onChange: (e) => field.handleChange(e.target.value as TData),
    onBlur: field.handleBlur,
    error: !field.state.meta.isValid,
    disabled: isSubmitting,
  };
}

interface UseReactFormPropsMapperReturn<TData> {
  error: boolean;
  id: string;
  name: string;
  helperText?: ReactNode;
  value: TData;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
  onBlur: () => void;
  disabled: boolean;
}

export function useReactFormPropsMapper<TData>(
  props: UseReactFormPropsMapperProps,
): UseReactFormPropsMapperReturn<TData> {
  const { helperText } = props;

  const field = useFieldContext<TData>();
  const isSubmitting = useStore(field.form.store, (state) => state.isSubmitting);
  const inputProps = useInputProps<TData>();

  return {
    error: inputProps.error,
    id: inputProps.id,
    name: inputProps.name,
    helperText: !inputProps.error ? helperText : last(field.state.meta.errors),
    value: inputProps.value,
    onChange: inputProps.onChange,
    onBlur: inputProps.onBlur,
    disabled: isSubmitting,
  };
}
